import {
  AnchorButton,
  Button,
  Colors,
  ControlGroup,
  Dialog,
  FormGroup,
  H4,
  HTMLSelect,
  InputGroup,
  Intent,
  Tag
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import Client from '../../../client'
import { LoadableSelect } from '../../../components/Inputs'
import Widget from '../../../components/Widget'
import config from '../../../config'
import { CurrentState } from '../../../current/types'
import { AppState } from '../../../store'
import { UprightStyle } from '../../../types'
import { showSuccess } from '../../../Toast'

const EmailValidationRegex = /^\S+@\S+\.\S+$/

// Keep in sync with User::MIN_CHECKED_IDENTITY_PART_LENGTH
const MIN_CHECKED_IDENTITY_PART_LENGTH = 3

export const validateEmail = (email: string) => email === '' || EmailValidationRegex.test(email)

type Props = RouteComponentProps<{ id: string }> & {
  current: CurrentState
  // true if we should display a limited page for editing the current user's account, http://localhost:3000/edit_account
  editAccount?: boolean
}

const styles: UprightStyle = {
  supplierValidation: { border: `1px solid ${Colors.RED3}` },
  userSelectNone: { userSelect: 'none' },
  flex: { display: 'flex' }
}

interface UserData {
  username: string
  name: string
  email: string
  password: string
  active: boolean
  role: string
  shopgoodwillUsername: string
  shopgoodwillPassword: string
  supplierId: number | null
  localStoreAttributes: Record<string, any> | null
  roleId: number | null
  lockedUntil: string | null
  lockoutReason: string | null
  failedLoginAttempts: number
  id?: number
}

interface NewUserState {
  mode: 'new' | 'edit'
  loading: boolean
  selectedRole: any
  roleInputDisabled: boolean
  error: any
  roles: any[]
  user: UserData
  id: number
}

function NewUser({ current, history, match, editAccount }: Props) {
  const [state, setState] = useState<NewUserState>({
    mode: 'new',
    loading: false,
    selectedRole: null,
    roleInputDisabled: false,
    error: null,
    roles: [],
    user: {
      username: '',
      name: '',
      email: '',
      password: '',
      active: true,
      role: 'user',
      shopgoodwillUsername: '',
      shopgoodwillPassword: '',
      supplierId: null,
      localStoreAttributes: null,
      roleId: null,
      lockedUntil: null,
      lockoutReason: null,
      failedLoginAttempts: 0
    },
    id: undefined
  })

  const [showPassword, setShowPassword] = useState(false)
  const [showResetMfaDialog, setShowResetMfaDialog] = useState(false)

  const fetchUser = useCallback(
    async (userId: number) => {
      try {
        const res = await Client.getUser(userId)

        setState(prev => {
          let selectedRole = prev.roles.find(role => role.id === res.data.user.roleId)

          // If default cannot be found, fallback to first available role. Default roles will always exist
          if (!selectedRole) {
            ;[selectedRole] = prev.roles
          }

          const userIsCurrentUser = current.user.id === res.data?.user?.id
          return {
            ...prev,
            user: res.data.user,
            selectedRole,
            roleInputDisabled: userIsCurrentUser
          }
        })
      } catch (error) {
        let displayError = (error as any)?.response?.data
        if (!displayError) {
          displayError = error as any // Handles errors like an HTTP 500 from the Lister API
        }
        setState(prev => ({ ...prev, error: displayError, loading: false }))
      }
    },
    [current.user.id]
  )

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await Client.v5GetRoles(false)

        // Default to user role when initially fetching role. This ensures the roles dropdown has a valid selection when creating a new user
        const { roles } = res.data
        if (roles.length === 0) {
          // Roles should always be returned. Default roles are available to all organizations.
          // This should not happen unless something went wrong with the api response. Raise an error.
          throw new Error('No roles found.')
        }

        setState(prev => {
          // "User" role will be changed to "Poster" for 2021 RBAC rollout.
          let selectedRole = roles.find(role => role.name === 'Poster')

          // If default cannot be found, fallback to first available role. Default roles will always exist
          if (!selectedRole) {
            // Pretty sure this ESLINT warning is incorrect
            // eslint-disable-next-line prefer-destructuring
            selectedRole = roles[0]
          }

          return { ...prev, roles, selectedRole }
        })
      } catch (_error) {
        setState(prev => ({
          ...prev,
          error: {
            message:
              "Something went wrong while fetching your organization's roles, please refresh the page and try again."
          },
          loading: false
        }))
      }
    }

    const run = async () => {
      let id = +match.params.id

      if (!id && editAccount && current.user.id) {
        id = +`${current.user.id}`
      }

      if (id) {
        setState(prev => ({ ...prev, mode: 'edit', loading: true, error: null, id }))
      }
      if (current.user.permissions.includes('settings.team')) {
        await fetchRoles()
      }
      if (id) {
        await fetchUser(id)
      }

      setState(prev => ({ ...prev, loading: false }))
    }

    run()
  }, [match.params, editAccount, current.user.id, current.user.permissions, fetchUser])

  const handleChange = (k: string, v: any) => {
    setState(prev => ({ ...prev, user: { ...prev.user, [k]: v } }))
  }

  const onRoleChangeEvent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const roleId = e.target.value ? +e.target.value : null
    setState(prev => {
      const selectedRole = prev.roles.find(role => role.id === roleId)
      const user = { ...prev.user, roleId: +roleId }

      if (selectedRole && !selectedRole.userPermissions.includes('products.post')) {
        // This is safe to do even if current doesn't have a sgw account (it's the default state)
        user.shopgoodwillUsername = ''
        user.shopgoodwillPassword = ''
      }
      return { ...prev, user, selectedRole }
    })
  }

  const onAssignSupplier = (supplierId: any) => {
    setState(prev => ({
      ...prev,
      user: {
        ...prev.user,
        supplierId,
        localStoreAttributes: supplierId ? prev.user.localStoreAttributes : null
      }
    }))
  }

  const handleLocalStoreChange = (field: string, val: any) => {
    setState(prev => ({
      ...prev,
      user: {
        ...prev.user,
        localStoreAttributes: prev.user.localStoreAttributes
          ? { ...prev.user.localStoreAttributes, [field]: val }
          : { [field]: val }
      }
    }))
  }

  const handleUnlock = async () => {
    const userId = state.user.id
    try {
      await Client.unlockUserAccount(userId)
      await fetchUser(userId)
      showSuccess('Account successfully unlocked')
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: (error as any).response?.data || {} }))
    }
  }

  const handleResetMfaConfirm = async () => {
    const userId = state.user.id
    setShowResetMfaDialog(false)
    try {
      await Client.resetUserMfa(userId)
      await fetchUser(userId)
      showSuccess('MFA successfully reset. User will be required to set up MFA again on next login.')
    } catch (error) {
      setState(prev => ({ ...prev, loading: false, error: (error as any).response?.data || {} }))
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const { mode, user, id } = state
    setState(prev => ({ ...prev, loading: true, error: null }))
    try {
      const action = mode === 'edit' ? Client.updateUser(id, user) : Client.createUser(user)
      await action
      setState(prev => ({ ...prev, loading: false }))
      if (editAccount) {
        showSuccess('Successfully updated your account')
      } else {
        history.push('/admin/users')
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: (error as any)?.response?.data
      }))
    }
    if (current.user.id === user.id && !editAccount) window.location.reload()
  }

  const isUserLocked = () => {
    const { lockedUntil } = state.user
    if (!lockedUntil) return false
    return new Date(lockedUntil) > new Date()
  }

  const { user, roles, selectedRole, error } = state
  const email = user.email?.trim() || ''
  const isEmailValid = validateEmail(email)
  const password = user.password || ''
  // Mirrors User#password_excludes_identity: no whitespace-separated part of the name or username
  // at least MIN_CHECKED_IDENTITY_PART_LENGTH characters long may appear in the password,
  // case-insensitively.
  const identityParts = `${user.name || ''} ${user.username || ''}`
    .split(/\s+/)
    .map(part => part.toLowerCase())
    .filter(part => part.length >= MIN_CHECKED_IDENTITY_PART_LENGTH)
  const passwordExcludesIdentity = !identityParts.some(part => password.toLowerCase().includes(part))
  const isPasswordValid =
    password.length >= 12 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password) &&
    /[^A-Za-z0-9]/.test(password) &&
    passwordExcludesIdentity
  const { permissions } = current.user

  const isSupplierRecommended = selectedRole && ['Store Manager', 'Store'].includes(selectedRole.name)
  const hasSupplierWarning = isSupplierRecommended && !user.supplierId

  let emailHelperText: string
  if (!email) {
    emailHelperText = 'Enables login with email and password resets'
  } else if (isEmailValid) {
    emailHelperText = 'Enables login with email and password resets'
  } else {
    emailHelperText = 'Not a valid email address'
  }

  const passwordRules = [
    { id: 'minLength', label: 'Be a minimum of 12 characters in length', valid: password.length >= 12 },
    { id: 'uppercase', label: 'Contain at least 1 uppercase letter', valid: /[A-Z]/.test(password) },
    { id: 'lowercase', label: 'Contain at least 1 lowercase letter', valid: /[a-z]/.test(password) },
    { id: 'number', label: 'Contain at least 1 number', valid: /\d/.test(password) },
    { id: 'special', label: 'Contain at least 1 special character', valid: /[^A-Za-z0-9]/.test(password) },
    { id: 'noIdentity', label: 'Not contain any part of the name or username', valid: passwordExcludesIdentity }
  ]

  let widgetTitle: string
  let submitButtonText: string
  if (editAccount) {
    widgetTitle = 'Edit Account'
    submitButtonText = 'Save'
  } else {
    widgetTitle = state.mode === 'new' ? 'Add Teammate' : 'Edit Teammate'
    submitButtonText = state.mode === 'new' ? 'Create teammate' : 'Update teammate'
  }

  return (
    <div className="container">
      <Widget
        breadcrumbs={
          !editAccount && [
            { to: '/admin', title: 'Admin' },
            { title: 'Teammates', to: '/admin/users' },
            { title: state.mode === 'new' ? 'New' : 'Edit' }
          ]
        }
        title={widgetTitle}
        loading={state.loading}
      >
        {error?.message && (
          <div className="bp3-callout bp3-intent-danger" style={{ marginBottom: '20px' }}>
            <h5 className="bp3-callout-title bp3-heading">{error.message}</h5>
          </div>
        )}

        {error?.errors?.password && (
          <div className="bp3-callout bp3-intent-danger" style={{ marginBottom: '20px' }}>
            <h5 className="bp3-callout-title bp3-heading">Password Error</h5>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {error.errors.password.map((err: string) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={submit} style={{ maxWidth: 300 }}>
          {permissions.includes('settings.team') && <H4>Basic information</H4>}
          <FormGroup label="Full name">
            <InputGroup
              value={user.name}
              onChange={e => handleChange('name', e.target.value)}
              data-testid="user-full-name-input"
            />
          </FormGroup>

          <FormGroup label="Username">
            <InputGroup value={user.username} onChange={e => handleChange('username', e.target.value)} />
          </FormGroup>

          <FormGroup
            label="Email"
            intent={email && !isEmailValid ? Intent.DANGER : Intent.NONE}
            helperText={emailHelperText}
          >
            <InputGroup autoComplete="off" value={email} onChange={e => handleChange('email', e.target.value)} />
          </FormGroup>

          <FormGroup
            label="Password"
            helperText={
              <div>
                {password && !isPasswordValid && (
                  <div style={{ color: Colors.RED3, marginBottom: 4 }}>Password does not meet criteria</div>
                )}

                <strong>Passwords must:</strong>
                <ul>
                  {passwordRules.map(rule => (
                    <li
                      key={rule.id}
                      data-testid={`password-rule-${rule.id}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 5
                      }}
                    >
                      <span>{rule.label}</span>
                      <span style={{ color: rule.valid ? 'green' : 'red', fontWeight: 600 }}>
                        {rule.valid ? '✔' : '✖'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            }
          >
            <InputGroup
              autoComplete="new-password"
              value={user.password}
              type={showPassword ? 'text' : 'password'}
              data-testid="user-password-input"
              onChange={e => handleChange('password', e.target.value)}
              rightElement={
                user.password ? (
                  <Button minimal small onClick={() => setShowPassword(prev => !prev)}>
                    <strong>{showPassword ? 'HIDE' : 'SHOW'}</strong>
                  </Button>
                ) : null
              }
            />
          </FormGroup>

          {permissions.includes('settings.team') && (
            <>
              <H4>Details</H4>
              <FormGroup
                label="Role"
                helperText={
                  current.user.id === user.id ? 'You may lose access to this page if you change your own role' : null
                }
              >
                {selectedRole && (
                  <ControlGroup>
                    <HTMLSelect
                      fill
                      value={selectedRole.id}
                      onChange={onRoleChangeEvent}
                      disabled={state.roleInputDisabled}
                      data-testid="role-select"
                    >
                      {roles.map(role => (
                        <option key={role.name} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </HTMLSelect>
                    {state.roleInputDisabled && (
                      <AnchorButton
                        style={styles.userSelectNone}
                        onClick={() => setState(prev => ({ ...prev, roleInputDisabled: !prev.roleInputDisabled }))}
                        data-testid="edit-role-unlock-button"
                      >
                        Edit
                      </AnchorButton>
                    )}
                  </ControlGroup>
                )}
              </FormGroup>

              <FormGroup
                label="Supplier"
                labelInfo={isSupplierRecommended ? 'optional, but encouraged for this role' : 'optional'}
                intent={hasSupplierWarning ? Intent.DANGER : Intent.NONE}
                helperText="Indicates a user that works for a supplier of your operation. They won't see manifests and orders from other suppliers"
              >
                <LoadableSelect
                  load={Client.suppliersIndex}
                  style={hasSupplierWarning ? styles.supplierValidation : undefined}
                  process={d => d.suppliers.map(u => ({ value: u.id, label: u.name }))}
                  onChange={onAssignSupplier}
                  value={user.supplierId}
                  placeholder="(None)"
                  noLabel
                  autoload={user.supplierId}
                />
              </FormGroup>

              {config.UPRIGHT_LOCAL() && user.supplierId && (
                <FormGroup
                  label="Upright Local store info footer"
                  labelInfo="optional"
                  helperText="A footer for products posted through Upright Local"
                >
                  <textarea
                    style={{ width: '100%', minHeight: 150 }}
                    className="bp3-input"
                    placeholder="Address and Store Hours"
                    value={user.localStoreAttributes?.storeInfo || ''}
                    onChange={v => handleLocalStoreChange('storeInfo', v.target.value)}
                  />
                </FormGroup>
              )}
              {current.organization.enabledMarkets.shopgoodwill &&
                selectedRole &&
                selectedRole.userPermissions.includes('products.post') && (
                  <>
                    <H4>Shopgoodwill login</H4>

                    <FormGroup label="Shopgoodwill username" labelInfo="optional">
                      <InputGroup
                        value={user.shopgoodwillUsername}
                        onChange={e => handleChange('shopgoodwillUsername', e.target.value)}
                      />
                    </FormGroup>
                    <FormGroup label="Shopgoodwill password" labelInfo="optional">
                      <InputGroup
                        type="password"
                        value={user.shopgoodwillPassword}
                        onChange={e => handleChange('shopgoodwillPassword', e.target.value)}
                      />
                    </FormGroup>
                  </>
                )}
              {state.mode !== 'new' && (
                <>
                  <H4>Activation status</H4>
                  <FormGroup
                    disabled={current.user.id === user.id}
                    label="Login enabled"
                    helperText="Whether or not this user is able to login and access Lister"
                  >
                    <HTMLSelect
                      fill
                      disabled={current.user.id === user.id}
                      onChange={v => {
                        const val = v.target.value === 'true'
                        setState(prev => ({ ...prev, user: { ...prev.user, active: val } }))
                      }}
                      value={user.active ? 'true' : 'false'}
                      options={[
                        // boolean values do not work for select values, so stringify them
                        { label: 'Active', value: 'true' },
                        { label: 'Inactive', value: 'false' }
                      ]}
                    />
                  </FormGroup>
                  {config.LOGIN_SECURITY() && (
                    <FormGroup label="Account lockout status">
                      {isUserLocked() ? (
                        <>
                          <div>
                            <Tag intent="warning" icon="lock" large round>
                              Locked
                            </Tag>
                            <div style={{ marginTop: 8, fontSize: 12, color: Colors.GRAY1 }}>
                              Locked until {new Date(user.lockedUntil as string).toLocaleString()}
                              <br />
                              Reason: {user.lockoutReason || 'Multiple failed login attempts'}
                              <br />
                              Failed attempts: {user.failedLoginAttempts || 0}
                            </div>
                          </div>
                          {permissions.includes('settings.team') && (
                            <Button icon="unlock" style={{ marginTop: 8 }} onClick={handleUnlock}>
                              Unlock account
                            </Button>
                          )}
                        </>
                      ) : (
                        <Tag intent="success" icon="unlock" large round>
                          Unlocked
                        </Tag>
                      )}
                    </FormGroup>
                  )}
                  {permissions.includes('settings.team') && (
                    <FormGroup
                      label="Multi-factor authentication"
                      helperText="Resetting MFA will require the user to set up MFA again on their next login"
                    >
                      <Button intent="warning" icon="key" onClick={() => setShowResetMfaDialog(true)}>
                        Reset MFA
                      </Button>
                      <Dialog
                        isOpen={showResetMfaDialog}
                        onClose={() => setShowResetMfaDialog(false)}
                        title="Reset Multi-Factor Authentication"
                        icon={IconNames.WARNING_SIGN}
                      >
                        <div className="bp3-dialog-body">
                          <h3>Are you sure you want to reset MFA for this user?</h3>
                          <p>
                            This will clear all Multi-Factor Authentication settings for{' '}
                            <strong>{user.name || user.username}</strong>. They will be required to set up Multi-Factor
                            Authentication again on their next login.
                          </p>
                          <p style={{ marginTop: 16, fontWeight: 600, color: Colors.ORANGE3 }}>
                            This action cannot be undone.
                          </p>
                        </div>
                        <div className="bp3-dialog-footer">
                          <div className="bp3-dialog-footer-actions">
                            <Button onClick={() => setShowResetMfaDialog(false)} minimal>
                              Cancel
                            </Button>
                            <Button intent={Intent.SUCCESS} onClick={handleResetMfaConfirm}>
                              Reset MFA
                            </Button>
                          </div>
                        </div>
                      </Dialog>
                    </FormGroup>
                  )}
                </>
              )}
            </>
          )}

          <Button
            intent="primary"
            type="submit"
            disabled={
              !user.name?.trim() || !user.username?.trim() || !isEmailValid || (password.length > 0 && !isPasswordValid)
            }
          >
            {submitButtonText}
          </Button>
        </form>
      </Widget>
    </div>
  )
}

export default connect((state: AppState) => ({ current: state.current }))(NewUser)
